import { AxiosRequestConfig } from 'axios';

import { apiService } from '../../shared/services/apiService';
import { SpringFiltration } from '../../shared/interfaces/SpringFiltration';
import { FoodType } from '../constants/foodType';
import { DatabaseFoodItem } from '../interfaces/DatabaseFoodItem';
import { FoodBrand } from '../interfaces/FoodBrand';
import { FoodDetails } from '../interfaces/FoodDetails';
import { FoodListItem } from '../interfaces/FoodListItem';
import { FoodTag } from '../interfaces/FoodTag';
import { FoodsFilterData } from '../interfaces/FoodsFilters';
import { MicroNutrient } from '../interfaces/MicroNutrient';
import { MicroNutrientOption } from '../interfaces/MicroNutrientOption';
import { SaveFoodRequest } from '../interfaces/SaveFoodRequest';

export type GetFoodsResponse = {
  page: number;
  /** exact on the search endpoint; the plain list uses "foods.size == limit" and over-reports a full last page */
  more: boolean;
  foods: FoodListItem[];
  totalElements: number;
  totalPages: number;
};

type RequestConfig = Pick<AxiosRequestConfig, 'isRunGlobalSpinner'>;

export type SearchDatabaseFoodsResponse = {
  foods: DatabaseFoodItem[];
  /** always null on this endpoint */
  timestamp_max: null;
  timestamp_min: null;
};

export const foodsService = {
  async getFoods(
    foodType: FoodType,
    { filters, requestedPage }: SpringFiltration<Partial<FoodsFilterData>>,
    config: RequestConfig = {},
  ): Promise<GetFoodsResponse> {
    const supplementType = 'default'; // hardcoded to remove active calendar supplements, this is deprecated functionality
    const { name = '', brand = '', excludeBanned } = filters || {};
    const pageParams = { page: requestedPage.page, limit: requestedPage.size };
    const banParams = excludeBanned ? { excludeBanned } : {};
    const q = `${brand} ${name}`.trim();

    // the search endpoint admits only global and own rows and ignores bans - the only server-side name search there is
    if (q) {
      const resp = await apiService.get<GetFoodsResponse>(
        `api/1.1.4/food/${foodType}/search`,
        { params: { ...pageParams, ...banParams, q, supplementType }, ...config },
      );

      return resp.data;
    }

    const resp = await apiService.get<GetFoodsResponse>(`api/1.1.4/food/${foodType}`, {
      params: { ...pageParams, ...banParams, supplementType },
      ...config,
    });

    return resp.data;
  },

  // the unversioned endpoint - a different data source from getFoods above: NutritionIx and its cache,
  // not the fs_food table, and no page/more/totalElements envelope. `src` is mandatory (400 without it)
  // but the backend ignores it while my_food_diary is true, so results are global, not type-scoped
  async searchDatabaseFoods(
    foodType: FoodType,
    query: string,
    config: RequestConfig = {},
  ): Promise<SearchDatabaseFoodsResponse> {
    const resp = await apiService.get<SearchDatabaseFoodsResponse>('api/food/search', {
      params: { q: query, src: foodType, my_food_diary: true },
      ...config,
    });

    return resp.data;
  },

  async changeFoodState(foodId: number, ban: boolean): Promise<void> {
    // the action string is matched case-sensitively; anything else 404s
    await apiService.put(`api/1.2.0/food/${foodId}`, null, {
      params: { action: ban ? 'ban' : 'unban' },
    });
  },

  async deleteFood(foodId: number): Promise<void> {
    await apiService.delete(`api/food/${foodId}`);
  },

  async getFood(foodId: number): Promise<FoodDetails> {
    const resp = await apiService.get<FoodDetails>(`api/web/food/${foodId}`);

    return resp.data;
  },

  // `type` overwrites the stored food type on update - the backend never checks it against the row
  async saveFood(foodType: FoodType, body: SaveFoodRequest): Promise<FoodDetails> {
    const resp = await apiService.post<FoodDetails>('api/web/food/manual', body, {
      params: { type: foodType },
    });

    return resp.data;
  },

  // replaces the whole set; `[]` deletes every micronutrient
  async updateMicroNutrients(foodId: number, nutrients: MicroNutrient[]): Promise<void> {
    await apiService.post(`api/web/food/update-micro-nutrients/${foodId}`, { nutrients });
  },

  // replaces the whole set; no tags also clears `recommended` on the backend
  async updateTags(foodId: number, tagIds: number[]): Promise<void> {
    await apiService.post(`api/web/food/update-tags/${foodId}`, null, {
      params: { 'tags[]': tagIds },
    });
  },

  async setRecommended(foodId: number, recommended: boolean): Promise<void> {
    await apiService.put(`api/web/food/recommended/${foodId}`, null, {
      params: { recommended },
    });
  },

  async getAllTags(): Promise<FoodTag[]> {
    const resp = await apiService.get<FoodTag[]>('api/web/food/tags/get-all');

    return resp.data;
  },

  async getAllMicroNutrients(): Promise<MicroNutrientOption[]> {
    const resp = await apiService.get<MicroNutrientOption[]>(
      'api/web/food/micro-nutrient/get-all',
    );

    return resp.data;
  },

  async getMeasurements(): Promise<string[]> {
    const resp = await apiService.get<{ measurements: string[] }>('api/measurements');

    return resp.data.measurements;
  },

  /** the backend defaults an omitted `foodType` to supplement */
  async getBrands(foodType?: FoodType): Promise<FoodBrand[]> {
    const resp = await apiService.get<FoodBrand[]>('api/food/brands', {
      params: { foodType },
    });

    return resp.data;
  },
};
